import React from 'react';
import { Link } from 'react-router-dom';
import { 
  Home, 
  User, 
  Sparkles, 
  BookOpen, 
  Mail, 
  Facebook, 
  Instagram, 
  Youtube, 
  X,
  CalendarCheck
} from 'lucide-react';

const Sidebar = ({ isOpen, toggleSidebar }) => {
  const [activeSection, setActiveSection] = React.useState('home');
  const [currentPath, setCurrentPath] = React.useState(window.location.pathname);

  React.useEffect(() => {
    const handleLocationChange = () => {
      setCurrentPath(window.location.pathname);
    };

    const handleSectionChange = (e) => {
      if (e.detail) {
        setActiveSection(e.detail);
      }
    };

    window.addEventListener('popstate', handleLocationChange);
    window.addEventListener('sectionchange', handleSectionChange);

    return () => {
      window.removeEventListener('popstate', handleLocationChange);
      window.removeEventListener('sectionchange', handleSectionChange);
    };
  }, []);

  const navItems = [
    { name: 'Home', path: '/', sectionId: 'home', icon: Home },
    { name: 'About', path: '/about', sectionId: 'about', icon: User },
    { name: 'Services', path: '/services', sectionId: 'services', icon: Sparkles },
    { name: 'Blog', path: '/blog', sectionId: 'blog', icon: BookOpen },
    { name: 'Contact', path: '/contact', sectionId: 'contact', icon: Mail },
    { name: 'Book Appointment', path: '/appointment', sectionId: 'appointment', icon: CalendarCheck, highlight: true },
  ];

  const handleNavClick = (e, item) => {
    if (isOpen) toggleSidebar();
    
    // If on home page and clicking a section link, smooth scroll to section without modifying URL hash
    if (window.location.pathname === '/' && item.sectionId) {
      const element = document.getElementById(item.sectionId);
      if (element) {
        e.preventDefault();
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        setActiveSection(item.sectionId);
      }
    }
  };

  const isItemActive = (item) => {
    if (currentPath === '/') {
      return activeSection === item.sectionId;
    }
    return currentPath === item.path;
  };

  const getNavItemPath = (item) => {
    return item.path;
  };

  return (
    <>
      {/* Mobile Overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-40 lg:hidden transition-opacity duration-300"
          onClick={toggleSidebar}
        />
      )}

      {/* Sidebar Container */}
      <aside 
        className={`fixed top-0 bottom-0 left-0 w-[280px] bg-white border-r border-slate-100 flex flex-col justify-between shadow-xl lg:shadow-none z-50 transition-all duration-300 cubic-bezier(0.16, 1, 0.3, 1)
          ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}
      >
        {/* Close Button - Mobile Only */}
        <button 
          onClick={toggleSidebar}
          className="absolute top-4 right-4 p-2 rounded-full hover:bg-slate-100 text-brand-navy lg:hidden transition-colors"
          aria-label="Close menu"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Top Section: Avatar & Watermark */}
        <div className="pt-12 pb-6 px-6 flex flex-col items-center relative overflow-hidden">
          {/* Watermark Name behind avatar */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none select-none z-0">
            <span className="text-4xl sm:text-5xl font-serif italic font-bold tracking-tight text-slate-100/90 whitespace-nowrap block rotate-[-12deg] capitalize">
              Prakash Upadhyaya
            </span>
          </div>

          {/* Profile Photo Placeholder with Animated Outer Ring */}
          <div className="relative z-10 group cursor-pointer mb-5">
            <div className="absolute -inset-1 rounded-full bg-gradient-to-tr from-brand-purple to-brand-teal opacity-75 blur-sm transition-all duration-500 group-hover:opacity-100 group-hover:scale-105 group-hover:rotate-180"></div>
            <div className="relative w-28 h-28 rounded-full border-4 border-white overflow-hidden shadow-lg transition-transform duration-500 group-hover:scale-105">
              <img
                src="/facesmall.jpg"
                alt="Prakash Upadhyaya"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>

          <div className="text-center relative z-10">
            <h2 className="text-xl font-bold text-brand-navy tracking-wide">Prakash Upadhyaya</h2>
            <p className="text-xs font-medium text-brand-purple tracking-widest uppercase mt-1">Vedic Pujari & Astrologer</p>
            <p className="text-[10px] text-slate-400 mt-0.5">Morayfield, QLD, Australia</p>
          </div>
        </div>

        {/* Navigation Section */}
        <nav className="px-4 flex-1 flex flex-col justify-center space-y-1">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = isItemActive(item);
            return (
              <Link
                key={item.name}
                to={getNavItemPath(item)}
                onClick={(e) => handleNavClick(e, item)}
                className={`
                  flex items-center gap-4 px-5 py-3 rounded-xl font-medium transition-all duration-300 relative group overflow-hidden
                  ${item.highlight && !isActive ? 'bg-gradient-to-r from-brand-purple/10 to-brand-teal/10 text-brand-purple border border-brand-purple/20' : ''}
                  ${isActive 
                    ? 'text-brand-purple bg-brand-purple/5 shadow-sm shadow-brand-purple/5 font-semibold' 
                    : !item.highlight ? 'text-slate-500 hover:text-brand-navy hover:bg-slate-50' : ''
                  }
                `}
              >
                <Icon className={`w-5 h-5 transition-transform duration-300 group-hover:scale-110
                  ${isActive ? 'text-brand-purple' : item.highlight ? 'text-brand-purple' : 'text-slate-400 group-hover:text-brand-navy'}
                `} />
                <span className="text-sm tracking-wide">{item.name}</span>
                
                {/* Highlight badge for Appointment */}
                {item.highlight && !isActive && (
                  <span className="ml-auto text-[9px] font-bold bg-brand-purple text-white px-1.5 py-0.5 rounded-full">NEW</span>
                )}

                {/* Sliding active indicator bar */}
                {isActive && (
                  <div className="absolute left-0 top-1/4 bottom-1/4 w-1 rounded-r-full bg-brand-purple transition-all duration-300" />
                )}
              </Link>
            );
          })}
        </nav>

        {/* Bottom Section: Socials & Copyright */}
        <div className="p-6 border-t border-slate-100 flex flex-col items-center">
          {/* Social Icons with Elegant Hovers */}
          <div className="flex items-center space-x-4 mb-4">
            <a 
              href="https://www.facebook.com/aditya.prakash.994357" 
              target="_blank" 
              rel="noopener noreferrer"
              className="w-9 h-9 rounded-full bg-slate-50 flex items-center justify-center text-slate-400 hover:bg-brand-navy hover:text-brand-teal transition-all duration-300 hover:-translate-y-1"
              aria-label="Facebook"
            >
              <Facebook className="w-4.5 h-4.5" />
            </a>
            <a 
              href="https://www.instagram.com/adityaprakash_aadi/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="w-9 h-9 rounded-full bg-slate-50 flex items-center justify-center text-slate-400 hover:bg-brand-navy hover:text-brand-teal transition-all duration-300 hover:-translate-y-1"
              aria-label="Instagram"
            >
              <Instagram className="w-4.5 h-4.5" />
            </a>
            <a 
              href="https://youtube.com" 
              target="_blank" 
              rel="noopener noreferrer"
              className="w-9 h-9 rounded-full bg-slate-50 flex items-center justify-center text-slate-400 hover:bg-brand-navy hover:text-brand-teal transition-all duration-300 hover:-translate-y-1"
              aria-label="YouTube"
            >
              <Youtube className="w-4.5 h-4.5" />
            </a>
          </div>

          {/* Copyright details */}
          <p className="text-[10px] text-slate-400 text-center font-medium leading-relaxed">
            &copy; 2024 All rights reserved<br />
            to <span className="text-brand-navy font-semibold">Prakash Upadhyaya</span>
          </p>

          {/* Subtle Admin Access Link */}
          <a
            href="/admin"
            className="mt-2 text-[9px] text-slate-300 hover:text-slate-500 transition-colors duration-200 tracking-widest uppercase"
            aria-label="Admin access"
          >
            · admin ·
          </a>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
